import React from "react";
import { Box, Button, Typography } from "@mui/material";
import chevronWhiteIcon from "@assets/facilityNetwork/ni-chevron-white.svg";
import chevronTealIcon from "@assets/facilityNetwork/ni-chevron-teal.svg";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import FacilityNetworkPageShell from "./FacilityNetworkPageShell";
import { FN } from "./facilityNetworkTheme";

export const MY_NETWORK_TALK_MAILTO =
  "mailto:contact@unicomply.ai?subject=My%20Network%20walkthrough";

const GRADIENT = "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)";

const primaryCtaSx = {
  textTransform: "none" as const,
  height: 32,
  minHeight: 32,
  px: 2,
  py: 1,
  borderRadius: "4px",
  fontFamily: FN.fontFamily,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "20px",
  color: "#fff",
  background: GRADIENT,
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
  "& .MuiButton-endIcon": { ml: "4px", mr: 0 },
  "&:hover": {
    background: "linear-gradient(90deg, #065b70 0%, #0a829d 100%)",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
  },
};

const onGradientCtaSx = {
  ...primaryCtaSx,
  color: FN.primary,
  background: "#fff",
  "&:hover": {
    background: "#f3fdff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
  },
};

function TalkToTeamButton({ variant }: { variant: "primary" | "onGradient" }) {
  const onGradient = variant === "onGradient";
  return (
    <Button
      component="a"
      href={MY_NETWORK_TALK_MAILTO}
      endIcon={
        <FacilityNetworkFigmaIcon
          src={onGradient ? chevronTealIcon : chevronWhiteIcon}
          size={12}
        />
      }
      sx={onGradient ? onGradientCtaSx : primaryCtaSx}
    >
      Talk to our team
    </Button>
  );
}

export default function MyNetworkGatePage() {
  return (
    <FacilityNetworkPageShell>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: FN.figmaTitleSize,
              fontWeight: 600,
              color: FN.title,
              letterSpacing: FN.figmaTitleTracking,
              lineHeight: "32px",
            }}
          >
            My Network
          </Typography>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 16,
              fontWeight: 600,
              color: FN.figmaNeutral,
              lineHeight: "22px",
            }}
          >
            See your consented facility hierarchy in one place.
          </Typography>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 12,
              fontWeight: 400,
              color: FN.figmaAccent,
              letterSpacing: "0.03px",
              lineHeight: "16px",
            }}
          >
            My Network is a sales-gated workspace for parent and child facilities
            you already operate. Talk with our team to enable it for your
            organization.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}>
          <TalkToTeamButton variant="primary" />
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 12,
              color: FN.figmaAccent,
              letterSpacing: "0.03px",
              lineHeight: "16px",
            }}
          >
            We will walk you through hierarchy linking and what Overview shows
            for your parent facility.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: 1,
            p: 4,
            borderRadius: FN.figmaCardRadius,
            background: GRADIENT,
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: FN.figmaTitleSize,
                fontWeight: 600,
                color: "#fff",
                letterSpacing: FN.figmaTitleTracking,
                lineHeight: "32px",
              }}
            >
              Ready to enable My Network?
            </Typography>
          </Box>
          <Box sx={{ flexShrink: 0, p: 2 }}>
            <TalkToTeamButton variant="onGradient" />
          </Box>
        </Box>
      </Box>
    </FacilityNetworkPageShell>
  );
}
